/**
 * PasswordInput.tsx — 带「显示/隐藏」toggle 的秘密输入框（单行 / 多行两种模式）
 *
 * 单行：从 auth 各页重复的裸 `<input type="password">` 提炼（login / accept-invite / password-reset 共用）。
 * 皮肤 1:1 对齐 design/Novel-admin/Login.html 的密码可见性 toggle：相对定位容器 + 右侧 40px 方钮，
 * 按 IconEye（隐藏态，点击可显示）/ IconEyeOff（显示态）切换；padding-right 留 42px 给图标让位。
 *
 * 多行（`multiline`）：PEM 私钥这类多行秘密（框架通道凭据 `secret` 字段，设计稿 PayerMax A2）。
 * `<textarea>` 没有 password 类型，隐藏态以 `-webkit-text-security: disc` 遮蔽；浏览器不支持该属性时
 * 改为文字透明 + 「内容已遮蔽」覆盖提示，不让私钥明文出现在屏幕上。右上角「显示 / 隐藏」ghost 小按钮切换；
 * 关闭拼写检查与自动补全，避免秘密进入浏览器词典或表单历史。
 *
 * 其余原生属性（id/placeholder/value/onChange/required…）透传，调用方按页用 `className` 套皮肤。
 */

import {
  type CSSProperties,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  useState,
} from 'react';
import { Icons } from '~/lib/icons';

interface SingleLinePasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  multiline?: false;
  /** 输入框样式类，默认 `input`；auth 页传 `input-big` 套登录皮肤 */
  className?: string;
}

interface MultiLinePasswordInputProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  multiline: true;
  className?: string;
}

export type PasswordInputProps = SingleLinePasswordInputProps | MultiLinePasswordInputProps;

/** 浏览器是否支持以圆点遮蔽 textarea 文本（非标准属性，按特性检测而非按 UA 判断）。 */
function supportsTextSecurity(): boolean {
  return (
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    CSS.supports('-webkit-text-security', 'disc')
  );
}

export function PasswordInput(props: PasswordInputProps) {
  const [revealed, setRevealed] = useState(false);

  if (props.multiline) {
    const { multiline: _multiline, className = 'input mono', style, ...rest } = props;
    const bullets = supportsTextSecurity();
    // csstype 未收录 -webkit-text-security，经 CSSProperties 断言透传；不支持时文字透明兜底
    const maskStyle = (
      revealed
        ? {}
        : bullets
          ? { WebkitTextSecurity: 'disc' }
          : { color: 'transparent', caretColor: 'var(--fg)' }
    ) as CSSProperties;
    const hasValue = typeof rest.value === 'string' ? rest.value !== '' : false;
    return (
      <div style={{ position: 'relative' }}>
        <textarea
          rows={3}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          {...rest}
          data-masked={revealed ? 'false' : 'true'}
          className={className}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            fontSize: 11,
            paddingRight: 56,
            ...maskStyle,
            ...style,
          }}
        />
        {!revealed && !bullets && hasValue && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 10,
              top: 8,
              fontSize: 11,
              color: 'var(--fg-muted)',
              pointerEvents: 'none',
            }}
          >
            内容已遮蔽
          </span>
        )}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          aria-label={revealed ? '隐藏内容' : '显示内容'}
          aria-pressed={revealed}
          onClick={() => setRevealed((v) => !v)}
          style={{ position: 'absolute', top: 4, right: 4 }}
        >
          {revealed ? '隐藏' : '显示'}
        </button>
      </div>
    );
  }

  const { multiline: _multiline, className = 'input', style, ...rest } = props;
  // 隐藏态显示「眼睛」（点击可揭示）；显示态显示「眼睛划线」（点击可隐藏）
  const ToggleIcon = revealed ? Icons.IconEyeOff : Icons.IconEye;

  return (
    <div style={{ position: 'relative' }}>
      <input
        {...rest}
        type={revealed ? 'text' : 'password'}
        className={className}
        style={{ width: '100%', boxSizing: 'border-box', paddingRight: 42, ...style }}
      />
      <button
        type="button"
        aria-label={revealed ? '隐藏密码' : '显示密码'}
        aria-pressed={revealed}
        onClick={() => setRevealed((v) => !v)}
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          width: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: 'var(--fg-muted)',
        }}
      >
        <ToggleIcon size={18} />
      </button>
    </div>
  );
}
